import type { StoryboardShot } from '../../services/studioService';

/**
 * BOARD LAYOUT — where every shot sits on the canvas, derived from the shots array.
 *
 * The board is a SEQUENCE, not a free-form graph: left-to-right IS the order the episode plays. So position is
 * computed from the array rather than stored per node, and dragging REORDERS the array instead of moving a node
 * to arbitrary coordinates. Two consequences worth stating, because they are the whole reason this file exists:
 *
 *  - A creator can never produce a board whose layout disagrees with the play order.
 *  - Reordering is a pure array edit; the canvas is a projection of it. Nothing to keep in sync.
 *
 * Scenes group consecutive shots that share a location (`continuesScene`). They are drawn as a BACKGROUND frame,
 * deliberately NOT as React Flow parent nodes — the maintainers advise against nested parent/child nodes for
 * this, and child-of-parent changes are not reported on drop, so parenting would fight us for no gain.
 */
export const NODE_W = 172;
/**
 * The node's real height, under the ComfyUI anatomy: title bar (~25) + a 9:16 preview at NODE_W
 * (172 x 16/9 = 306) + the widget row holding the line (~65). Kept as a constant because the scene frames
 * are sized from it; the browser check in the board journey asserts the frames still enclose their nodes,
 * so a drift here fails loudly rather than silently cropping the cards.
 */
export const NODE_H = 396;
export const SHOT_GAP = 30;
export const SCENE_GAP = 96;   // the visible pause between scenes — a cut, not a continuation
export const SCENE_PAD = 18;
export const ROW_Y = 60;

export interface SceneBand {
  /** Index of the first shot in this scene, and how many it holds. */
  start: number;
  count: number;
  x: number;
  width: number;
  label: string;
}

/** Group consecutive shots into scenes. A shot with `continuesScene` stays in the running scene. */
export function sceneBands(shots: StoryboardShot[]): SceneBand[] {
  const bands: SceneBand[] = [];
  shots.forEach((s, i) => {
    const open = bands[bands.length - 1];
    if (i > 0 && s.continuesScene && open) open.count += 1;
    else bands.push({ start: i, count: 1, x: 0, width: 0, label: `Scene ${bands.length + 1}` });
  });
  // Lay the bands out left to right, adding the scene gap between them.
  let x = 0;
  for (const b of bands) {
    b.x = x;
    b.width = b.count * NODE_W + (b.count - 1) * SHOT_GAP + SCENE_PAD * 2;
    x += b.width + SCENE_GAP;
  }
  return bands;
}

/** The x position of the shot at `index`, accounting for the scene it falls in. */
export function shotX(shots: StoryboardShot[], index: number, bands = sceneBands(shots)): number {
  const b = bands.find((band) => index >= band.start && index < band.start + band.count) || bands[0];
  if (!b) return 0;
  return b.x + SCENE_PAD + (index - b.start) * (NODE_W + SHOT_GAP);
}

/**
 * Where a shot dropped at `dropX` should land in the array. Uses each slot's CENTRE, so a card swaps as soon as
 * it passes the midpoint of its neighbour — the behaviour a creator expects from every sortable list.
 * `dragIndex` is excluded so a shot never counts itself when working out where it goes.
 */
export function dropIndex(shots: StoryboardShot[], dragIndex: number, dropX: number): number {
  const bands = sceneBands(shots);
  const centres = shots
    .map((_, i) => ({ i, cx: shotX(shots, i, bands) + NODE_W / 2 }))
    .filter((c) => c.i !== dragIndex);
  // `>=`, not `>`: a card dropped exactly onto a slot's centre should TAKE that slot rather than stop short
  // of it. With `>` a drag to the last position landed one slot early, which felt like the drop was ignored.
  const dropCentre = dropX + NODE_W / 2;
  const before = centres.filter((c) => dropCentre >= c.cx).length;
  return Math.max(0, Math.min(shots.length - 1, before));
}

/** Move an item within an array — the single mutation a reorder performs. */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item);
  return next;
}
